<script setup lang="ts">
import {
  DataPointDetailsDto,
  EnumerationEvaluationOptionDto,
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  instanceOfEnumerationEvaluationResultDto,
  instanceOfNumericEvaluationParameterDto,
  instanceOfNumericEvaluationResultDto,
  ResultHistoryEntryDto,
  TrackItTaskWorkResultDto,
  TrackItTaskWorkStepDto,
} from '@/api/generator';
import { useFormMode } from '@/components/forms/useFormMode';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { mdiAccountEdit, mdiClose, mdiEyeOff, mdiEye, mdiPlus } from '@mdi/js';
import { edit } from '@/components/icons/icons';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { I18nKey } from '@/utils/typings';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import JobExecutionTrendViewDialog from '../JobExecutionTrendViewDialog.vue';
import EnumInput from './manual/EnumInput.vue';
import ManualTaskResult from './manual/ManualTaskResult.vue';
import NumericInput from './manual/NumericInput.vue';
import { useEditingMode } from './manual/useEditingMode';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { fullName } from '@/utils/helper';
import { useJobOccurrence } from '../../utils/useJobOccurrence';
import { useTrackItManualValue } from '../../utils/useTrackItManualValue';
import { computed } from 'vue';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import Button from '@/components/buttons/Button.vue';
import TrackItDataPointsTable from './TrackItDataPointsTable.vue';
import TrackItMeasurementInfoDetails from './TrackItMeasurementInfoDetails.vue';

const props = defineProps<{
  step: TrackItTaskWorkStepDto;
}>();

const mode = useFormMode();

const { jobOccurrence, toggleIgnoreResult, reload } = useJobOccurrence();

const { submitting, submitManualValue } = useTrackItManualValue();

const hasMeasurements = computed(() => props.step.results?.length);

// A Track-it result's history spans all its evaluations; each sheet shows only its own entries.
function historyForEvaluation(
  result: TrackItTaskWorkResultDto,
  evaluationParameterId: string,
): ResultHistoryEntryDto[] {
  return (result.history ?? []).filter(
    (entry) => entry.evaluationParameterId === evaluationParameterId,
  );
}

const { isEditingAnyResult, enableEditing, disableEditing } = useEditingMode();

function isEditable(result: TrackItTaskWorkResultDto, evaluation: EvaluationParameterDto) {
  return (result.unmappedEvaluationIds ?? []).includes(evaluation.id);
}

function isInputDisabled(result: TrackItTaskWorkResultDto) {
  return submitting.value || result.state === 'Ignored' || isEditingAnyResult.value;
}

function valueDetails(
  result: TrackItTaskWorkResultDto,
  evaluation: EvaluationParameterDto,
): DataPointDetailsDto | undefined {
  const evaluationResult = result.evaluationResults?.find(
    (r) => r.evaluationParameterId === evaluation.id,
  );
  const history = historyForEvaluation(result, evaluation.id);
  if (
    instanceOfNumericEvaluationParameterDto(evaluation) &&
    evaluationResult &&
    instanceOfNumericEvaluationResultDto(evaluationResult) &&
    evaluationResult.deviationValue != null
  ) {
    return { evaluationResult, evaluationParameter: evaluation, history };
  }
  if (
    instanceOfEnumerationEvaluationParameterDto(evaluation) &&
    evaluationResult &&
    instanceOfEnumerationEvaluationResultDto(evaluationResult) &&
    evaluationResult.selectedOption
  ) {
    return { evaluationResult, evaluationParameter: evaluation, history };
  }
  return undefined;
}

async function onManualValue(
  result: TrackItTaskWorkResultDto,
  evaluation: EvaluationParameterDto,
  value: number | EnumerationEvaluationOptionDto | null,
) {
  if (!jobOccurrence.value.id) {
    return;
  }

  const updated = await submitManualValue(
    result,
    evaluation,
    value,
    props.step.id,
    jobOccurrence.value.id,
  );

  if (updated) {
    await reload();
  }
}
</script>

<template>
  <v-row>
    <v-col>
      <h5>{{ $t('Measurement_one') }}</h5>

      <p v-if="!hasMeasurements">{{ $t('NoMeasurements') }}</p>

      <v-expansion-panels v-else>
        <v-expansion-panel v-for="result in step.results" :key="result.id">
          <template #title>
            <div class="d-flex align-center w-100 mr-3">
              <v-icon :icon="mdiPlus" class="mr-2" />

              <div class="me-auto">
                <TimeStamp
                  :time="result.creationTime"
                  :user="fullName(result.creatorUser ?? undefined)"
                  :singleLine="true"
                />
              </div>

              <div class="d-flex align-center">
                <EvaluationState
                  :value="result.output || undefined"
                  :disabled="result.state === 'Ignored'"
                />
              </div>
            </div>
          </template>

          <template #text>
            <div class="d-flex align-center justify-end">
              <CommentInlineDialog
                :entity="result"
                entityWithCommentType="WorkResult"
                :disabled="result.state === 'Ignored' || isEditingAnyResult"
                @change="reload()"
              />

              <Tooltip>
                <template #tooltip>
                  {{ result.state === 'Ignored' ? $t('Ignored') : $t('Acknowledged') }}
                </template>
                <template #target="{ props: iconBtnProps }">
                  <Button
                    v-if="mode === 'execution'"
                    :icon="result.state === 'Ignored' ? mdiEyeOff : mdiEye"
                    variant="text"
                    :aria-label="result.state === 'Ignored' ? $t('Ignored') : $t('Acknowledged')"
                    v-bind="iconBtnProps"
                    @click.stop="toggleIgnoreResult(result)"
                  />
                </template>
              </Tooltip>
            </div>

            <v-sheet
              v-for="evaluation in step.evaluations ?? []"
              :key="evaluation.id"
              border
              class="pa-3 mb-4 elevation-2"
            >
              <v-row>
                <v-col cols="4">
                  <v-row>
                    <v-col cols="6">
                      <v-label>
                        {{
                          $t(
                            instanceOfEnumerationEvaluationParameterDto(evaluation)
                              ? 'Enumeration_one'
                              : 'Quantity_one',
                          )
                        }}
                      </v-label>
                      <div>
                        <IdentifierLabel :label="getSetupParameterName(evaluation)" />
                      </div>
                    </v-col>

                    <v-col
                      v-if="
                        instanceOfNumericEvaluationParameterDto(evaluation) &&
                        evaluation.evaluationType
                      "
                      cols="6"
                    >
                      <v-label>{{ $t('EvaluationType_one') }}</v-label>
                      <div>
                        {{ $t(`EvaluationTypeEnum.${evaluation.evaluationType}` as I18nKey) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col class="align-center">
                  <ManualTaskResult
                    v-if="valueDetails(result, evaluation)"
                    :result="valueDetails(result, evaluation)!"
                    :evaluation="evaluation"
                    :ignored="result.state === 'Ignored'"
                    :hideHistory="
                      !isEditable(result, evaluation) &&
                      !historyForEvaluation(result, evaluation.id).length
                    "
                    class="executionPanel"
                    @enableEditing="enableEditing"
                    @disableEditing="disableEditing"
                  >
                    <template #edit="{ toggleInEditing, evaluationResult }">
                      <div class="d-flex align-end">
                        <EnumInput
                          v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)"
                          :evaluation="evaluation"
                          :result="
                            instanceOfEnumerationEvaluationResultDto(evaluationResult)
                              ? evaluationResult
                              : undefined
                          "
                          :disabled="submitting"
                          @input="
                            onManualValue(result, evaluation, $event);
                            toggleInEditing(false);
                          "
                        />

                        <NumericInput
                          v-if="
                            instanceOfNumericEvaluationParameterDto(evaluation) &&
                            instanceOfNumericEvaluationResultDto(evaluationResult)
                          "
                          :evaluation="evaluation"
                          :value="evaluationResult.deviationValue ?? undefined"
                          :disabled="submitting"
                          allowClear
                          @input="
                            onManualValue(result, evaluation, $event);
                            toggleInEditing(false);
                          "
                          @cancel="toggleInEditing(false)"
                        />

                        <Tooltip>
                          <template #target="{ props: targetProps }">
                            <Button
                              v-bind="targetProps"
                              class="cancel-button"
                              :aria-label="$t('Cancel')"
                              tabindex="-1"
                              variant="text"
                              size="small"
                              :icon="mdiClose"
                              @mousedown.prevent
                              @click="toggleInEditing(false)"
                            />
                          </template>
                          <template #tooltip>{{ $t('Cancel') }}</template>
                        </Tooltip>
                      </div>
                    </template>

                    <template #value-append>
                      <!-- The result has no "entered by hand" flag; only unmapped (editable) evaluations get one. -->
                      <Tooltip v-if="isEditable(result, evaluation)">
                        <template #target="{ props: targetProps }">
                          <v-icon
                            v-bind="targetProps"
                            :icon="mdiAccountEdit"
                            size="small"
                            class="ml-1"
                          />
                        </template>
                        <template #tooltip>{{ $t('ManuallyEnteredValue' as I18nKey) }}</template>
                      </Tooltip>
                    </template>

                    <template #actions="{ evaluationResult, toggleInEditing, disabled }">
                      <div class="mr-3 d-flex align-center">
                        <JobExecutionTrendViewDialog
                          :evaluationResultId="evaluationResult.id"
                          :disabled="disabled || result.state === 'Ignored' || isEditingAnyResult"
                        />

                        <CommentInlineDialog
                          :entity="evaluationResult"
                          entityWithCommentType="EvaluationResultOrDataPoint"
                          :disabled="disabled || isEditingAnyResult"
                          @change="reload()"
                        />

                        <Tooltip v-if="mode === 'execution' && isEditable(result, evaluation)">
                          <template #target="{ props: targetProps }">
                            <Button
                              v-bind="targetProps"
                              variant="text"
                              :icon="edit"
                              class="ml-6"
                              :aria-label="$t('EnterValue')"
                              :disabled="
                                disabled || result.state === 'Ignored' || isEditingAnyResult
                              "
                              @click.stop="toggleInEditing()"
                            />
                          </template>
                          <template #tooltip>{{ $t('Edit') }}</template>
                        </Tooltip>
                      </div>
                    </template>
                  </ManualTaskResult>

                  <div
                    v-else-if="mode === 'execution' && isEditable(result, evaluation)"
                    class="d-flex align-end executionPanel"
                  >
                    <EnumInput
                      v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)"
                      :evaluation="evaluation"
                      :disabled="isInputDisabled(result)"
                      @input="onManualValue(result, evaluation, $event)"
                    />

                    <div v-else class="w-25">
                      <NumericInput
                        v-if="instanceOfNumericEvaluationParameterDto(evaluation)"
                        :evaluation="evaluation"
                        :disabled="isInputDisabled(result)"
                        @input="onManualValue(result, evaluation, $event)"
                      />
                    </div>
                  </div>

                  <div v-else class="executionPanel">
                    <FormLabel
                      :label="
                        $t(
                          instanceOfEnumerationEvaluationParameterDto(evaluation)
                            ? 'ManualInput'
                            : 'Value',
                        )
                      "
                    >
                      {{ EMPTY_FIELD_SIGN }}
                    </FormLabel>
                  </div>
                </v-col>
              </v-row>
            </v-sheet>

            <v-row v-if="result.trackItDataTypesWithoutEvaluation?.length">
              <v-col>
                <TrackItDataPointsTable
                  title="Track-It Data Points without Evaluation"
                  :dataPoints="result.trackItDataTypesWithoutEvaluation"
                />
              </v-col>
            </v-row>

            <v-row v-if="result.trackItMeasurement">
              <v-col>
                <TrackItMeasurementInfoDetails :measurement="result.trackItMeasurement" />
              </v-col>
            </v-row>
          </template>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
  </v-row>
</template>
